/**
 * Prepares one Outlook draft and reports what the user has to do next.
 *
 * The app never sends mail. The draft opens in Outlook, where the user
 * chooses the recipients and subject and presses Send.
 */
import { useCallback, useState } from "react";
import { devTrace } from "../../services/devTrace";
import { downloadBlob } from "./delivery";
import type { EmailDocument } from "./emailDocument";
import { buildEml, safeFilename, type EmailAttachment } from "./eml";
import { EmailDraftError, draftErrorMessage } from "./errors";

export type EmailSourceKind = "answer" | "dashboard" | "document";

export interface EmailDraftSource {
  document: EmailDocument;
  /** The report to attach. Absent for a plain answer. */
  attachment?: () => Promise<EmailAttachment>;
}

export interface EmailDraftNotice {
  tone: "success" | "warning";
  text: string;
}

async function loadAttachment(source: EmailDraftSource): Promise<EmailAttachment | null> {
  if (!source.attachment) return null;
  try {
    return await source.attachment();
  } catch (cause) {
    throw new EmailDraftError(
      "attachment_failed",
      "The report attachment could not be produced.",
      cause,
    );
  }
}

export function useEmailDraft(input: {
  traceId: string;
  kind: EmailSourceKind;
  prepare: () => EmailDraftSource | Promise<EmailDraftSource>;
}) {
  const { traceId, kind, prepare } = input;
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<EmailDraftNotice | null>(null);

  const download = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const started = performance.now();
    try {
      const source = await prepare();
      const { document } = source;
      // Partial success is still useful: a draft without its PDF beats no
      // draft, so a failed attachment is reported rather than fatal.
      let attachment: EmailAttachment | null = null;
      let attachmentError = "";
      try {
        attachment = await loadAttachment(source);
      } catch (cause) {
        attachmentError = draftErrorMessage(cause);
      }
      const eml = buildEml(document, attachment ? [attachment] : []);
      downloadBlob(
        new Blob([eml], { type: "message/rfc822" }),
        safeFilename(document.title, "eml"),
      );
      setNotice({
        tone: attachmentError ? "warning" : "success",
        text: attachmentError
          ? `Draft downloaded without the report: ${attachmentError} Open the .eml file to edit it in Outlook.`
          : "Draft downloaded. Open the .eml file to edit it in Outlook, then add the recipients and a subject.",
      });
      devTrace.record({
        runId: traceId,
        step: "email_draft_downloaded",
        actor: "Email",
        level: attachmentError ? "warning" : "success",
        summary: "Downloaded an Outlook draft.",
        output: {
          kind,
          blocks: document.blocks.length,
          attached: Boolean(attachment),
          durationMs: Math.round(performance.now() - started),
        },
      });
    } catch (cause) {
      setNotice({ tone: "warning", text: draftErrorMessage(cause) });
      devTrace.record({
        runId: traceId,
        step: "email_draft_failed",
        actor: "Email",
        level: "error",
        summary: "The email draft could not be prepared.",
        output: {
          kind,
          code: cause instanceof EmailDraftError ? cause.code : "unexpected",
        },
      });
      if (!(cause instanceof EmailDraftError)) {
        console.error("Email draft failed", cause);
      }
    } finally {
      setBusy(false);
    }
  }, [busy, kind, prepare, traceId]);

  return { busy, notice, download, dismiss: () => setNotice(null) };
}
